import React, { useState, useMemo, useEffect } from 'react';
import {
  formatCurrency,
  extractMonetaryValue,
  filterLineItemsByTimeRange,
  getCategoryKey,
  getDetailProductKey,
  getProjectKey,
  getTimeRangeLabel,
  parseMonthPeriod
} from '../utils/dataUtils';
import { toCsv, toFileSlug, downloadTextFile } from '../utils/downloadUtils';
import { useCurrency } from '../hooks/useCurrency';
import { Icon } from './Icon';

const PAGE_SIZE = 50;
// Above this many rows, groups start collapsed so the page stays responsive.
const AUTO_EXPAND_LIMIT = 200;

const COLUMNS = [
  { field: 'description', label: 'Description' },
  { field: 'product', label: 'Product' },
  { field: 'project_name', label: 'Project' },
  { field: 'invoice_period', label: 'Period' },
  { field: 'hours', label: 'Hours', numeric: true },
  { field: 'amount', label: 'Amount', numeric: true }
];

// Match used when the caller does not say which chart the selection came
// from: exact match on any descriptive field, then partial matches.
const matchesLoosely = (item, selected) => {
  const fields = [item.product, item.category, item.name, item.description, item.group_description]
    .map((value) => (value === null || value === undefined ? '' : String(value)));
  if (fields.some((value) => value === selected)) return true;

  const product = fields[0];
  if (product && (product.includes(selected) || selected.includes(product))) return true;
  return fields.slice(1).some((value) => value && value.includes(selected));
};

const sortValue = (item, field) => {
  if (field === 'amount') return extractMonetaryValue(item);
  if (field === 'hours') return parseFloat(item.hours) || 0;
  if (field === 'project_name') return getProjectKey(item).toLowerCase();
  const value = item[field];
  return value === null || value === undefined ? '' : String(value).toLowerCase();
};

const groupValue = (item, field) => {
  if (field === 'project') return getProjectKey(item);
  if (field === 'month') {
    if (item.invoice_period) return item.invoice_period;
    if (item.start) {
      const date = new Date(item.start);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
      }
    }
    return 'Unknown Date';
  }
  return item[field] || 'Unknown';
};

export const LineItemExplorer = ({
  detailedLineItems,
  selectedCategory,
  selectedProject,
  drillDownType,
  onBack,
  timeRange
}) => {
  const [sortField, setSortField] = useState('amount');
  const [sortDirection, setSortDirection] = useState('desc');
  const [groupBy, setGroupBy] = useState('none');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [openGroups, setOpenGroups] = useState({});
  useCurrency();

  const selection = selectedProject || selectedCategory;
  const type = selectedProject ? 'project' : drillDownType;

  // Rows behind the selected chart segment, for the current time range
  const matchingItems = useMemo(() => {
    if (!detailedLineItems || detailedLineItems.length === 0 || !selection) return [];

    const timeFilteredItems = filterLineItemsByTimeRange(detailedLineItems, timeRange);

    if (type === 'project') return timeFilteredItems.filter((item) => getProjectKey(item) === selection);
    if (type === 'category') return timeFilteredItems.filter((item) => getCategoryKey(item) === selection);
    if (type === 'product') return timeFilteredItems.filter((item) => getDetailProductKey(item) === selection);
    return timeFilteredItems.filter((item) => matchesLoosely(item, selection));
  }, [detailedLineItems, selection, type, timeRange]);

  // Search and sort
  const filteredItems = useMemo(() => {
    let items = matchingItems;

    const searchLower = searchTerm.trim().toLowerCase();
    if (searchLower) {
      items = items.filter((item) =>
        Object.entries(item).some(([key, value]) => {
          if (key === 'invoice_uuid' || key === 'invoice_amount') return false;
          return String(value).toLowerCase().includes(searchLower);
        })
      );
    }

    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const valueA = sortValue(a, sortField);
      const valueB = sortValue(b, sortField);
      if (valueA < valueB) return -1 * direction;
      if (valueA > valueB) return 1 * direction;
      return 0;
    });
  }, [matchingItems, searchTerm, sortField, sortDirection]);

  const totalAmount = useMemo(
    () => filteredItems.reduce((sum, item) => sum + extractMonetaryValue(item), 0),
    [filteredItems]
  );

  const groupedData = useMemo(() => {
    if (groupBy === 'none') return null;
    const grouped = {};
    filteredItems.forEach((item) => {
      const key = groupValue(item, groupBy);
      if (!grouped[key]) grouped[key] = { items: [], totalAmount: 0 };
      grouped[key].items.push(item);
      grouped[key].totalAmount += extractMonetaryValue(item);
    });
    const entries = Object.entries(grouped);
    if (groupBy === 'month') {
      // Months read best in date order, newest first
      return entries.sort(([a], [b]) => parseMonthPeriod(b) - parseMonthPeriod(a));
    }
    return entries.sort(([, a], [, b]) => b.totalAmount - a.totalAmount);
  }, [filteredItems, groupBy]);

  // Back to the first page whenever the visible set changes
  useEffect(() => {
    setPage(1);
    setOpenGroups({});
  }, [selection, type, timeRange, searchTerm, groupBy]);

  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filteredItems.slice(pageStart, pageStart + PAGE_SIZE);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(1);
  };

  const downloadAsCSV = () => {
    if (filteredItems.length === 0) return;
    downloadTextFile(`${toFileSlug(selection)}_details_${timeRange}.csv`, toCsv(filteredItems));
  };

  const expandGroupsByDefault = filteredItems.length <= AUTO_EXPAND_LIMIT;
  const isGroupOpen = (name) => (name in openGroups ? openGroups[name] : expandGroupsByDefault);
  const toggleGroup = (name) => setOpenGroups((prev) => ({ ...prev, [name]: !isGroupOpen(name) }));

  const renderHeader = (columns) => (
    <thead>
      <tr>
        {columns.map((column) => {
          const isSorted = sortField === column.field;
          return (
            <th
              key={column.field}
              scope="col"
              className={column.numeric ? 'numeric' : undefined}
              aria-sort={isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              <button type="button" className="sort-btn" onClick={() => handleSort(column.field)}>
                {column.label}
                <span aria-hidden="true">{isSorted ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}</span>
              </button>
            </th>
          );
        })}
      </tr>
    </thead>
  );

  const renderCell = (item, column) => {
    switch (column.field) {
      case 'description': return item.description || 'No description';
      case 'product': return item.product || 'N/A';
      case 'project_name': return getProjectKey(item);
      case 'invoice_period': return item.invoice_period || 'N/A';
      case 'hours': return item.hours || 'N/A';
      default: return formatCurrency(extractMonetaryValue(item));
    }
  };

  const renderRows = (items, columns) =>
    items.map((item, index) => (
      <tr key={`${item.invoice_uuid || ''}-${index}`}>
        {columns.map((column) => (
          <td key={column.field} className={column.numeric ? 'numeric' : undefined}>
            {renderCell(item, column)}
          </td>
        ))}
      </tr>
    ));

  // The grouped-by column would repeat the group heading on every row
  const groupedColumns = COLUMNS.filter((column) => {
    if (groupBy === 'project') return column.field !== 'project_name';
    if (groupBy === 'month') return column.field !== 'invoice_period';
    if (groupBy === 'product') return column.field !== 'product';
    return true;
  });

  const hasSearch = searchTerm.trim().length > 0;

  return (
    <div className="line-item-explorer">
      {/* Header with controls */}
      <div className="explorer-header">
        <h3>
          {selection} Details
          <span className="explorer-summary">
            ({filteredItems.length} items, {formatCurrency(totalAmount)})
          </span>
        </h3>
        <div className="controls">
          <button type="button" onClick={onBack} className="back-btn">
            <Icon name="arrowLeft" />
            Back to Chart
          </button>
          <button
            type="button"
            onClick={downloadAsCSV}
            className="download-btn"
            disabled={filteredItems.length === 0}
          >
            <Icon name="download" />
            Download CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          type="search"
          placeholder="Search descriptions, projects..."
          aria-label="Search line items"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          aria-label="Group line items"
        >
          <option value="none">No Grouping</option>
          <option value="project">Group by Project</option>
          <option value="month">Group by Month</option>
          <option value="product">Group by Product</option>
          <option value="category">Group by Category</option>
        </select>
      </div>

      <div className="explorer-range">
        Showing data for {getTimeRangeLabel(timeRange)}
      </div>

      {filteredItems.length === 0 ? (
        <div className="empty-state">
          {hasSearch ? (
            <>
              <p><strong>No line items match "{searchTerm.trim()}".</strong></p>
              <p>Check the spelling or clear the search to see all {matchingItems.length} items.</p>
              <button type="button" className="btn-secondary" onClick={() => setSearchTerm('')}>Clear search</button>
            </>
          ) : (
            <>
              <p><strong>No detailed line items found for {selection}.</strong></p>
              <p>There is no data for it in {getTimeRangeLabel(timeRange)}. Try a longer time range.</p>
            </>
          )}
        </div>
      ) : groupedData ? (
        <div className="grouped-data">
          {!expandGroupsByDefault && (
            <p className="explorer-range">Select a group to show its line items.</p>
          )}
          {groupedData.map(([groupName, group]) => {
            const open = isGroupOpen(groupName);
            return (
              <div key={groupName} className="group-section">
                <h4>
                  <button
                    type="button"
                    className="group-toggle"
                    onClick={() => toggleGroup(groupName)}
                    aria-expanded={open}
                  >
                    <span><span aria-hidden="true">{open ? '▾' : '▸'}</span> {groupName} <span className="group-count">({group.items.length})</span></span>
                    <span>{formatCurrency(group.totalAmount)}</span>
                  </button>
                </h4>

                {open && (
                  <div className="table-scroll">
                    <table>
                      {renderHeader(groupedColumns)}
                      <tbody>{renderRows(group.items, groupedColumns)}</tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div className="table-scroll">
            <table>
              {renderHeader(COLUMNS)}
              <tbody>{renderRows(pageItems, COLUMNS)}</tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <nav className="pagination" aria-label="Line item pages">
              <button type="button" className="btn-secondary" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
                Previous
              </button>
              <span aria-live="polite">
                {pageStart + 1}–{pageStart + pageItems.length} of {filteredItems.length}
              </span>
              <button type="button" className="btn-secondary" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount}>
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};
